import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { api, fmtMoney, STAGE_META, formatError, isAdmin } from '../api';
import { useAuth } from '../AuthContext';
import NewDealModal from '../components/NewDealModal';

const STAGE_ORDER = [
  'new_lead', 'contacted', 'nurture', 'proposal_sent', 'drawings_requested', 'drawings_received',
  'estimator_assigned', 'quote_sent', 'approved_deposit', 'in_progress', 'delivered',
  'final_paid', 'closed_won', 'revisions', 'lost',
];

export default function Pipeline() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [drag, setDrag] = useState(null);
  const [hoverStage, setHoverStage] = useState(null);
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/deals?limit=500');
      setDeals(data);
    } catch (e) { setErr(formatError(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const onDragStart = (deal) => setDrag(deal);
  const onDragEnd = () => { setDrag(null); setHoverStage(null); };
  const onDrop = async (stage) => {
    if (!drag || drag.stage === stage) { onDragEnd(); return; }
    // optimistic update
    setDeals((ds) => ds.map((d) => (d.id === drag.id ? { ...d, stage } : d)));
    onDragEnd();
    try {
      await api.put(`/deals/${drag.id}`, { stage });
    } catch (e) {
      setErr(formatError(e));
      reload();
    }
  };

  const grouped = STAGE_ORDER.reduce((acc, s) => { acc[s] = []; return acc; }, {});
  deals.forEach((d) => { if (grouped[d.stage]) grouped[d.stage].push(d); });

  return (
    <div data-testid="crm-pipeline">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <p style={{ color: 'var(--crm-muted)', fontSize: 13, margin: 0 }}>
          Drag a deal card between columns to move it to a new stage. Click a card to open it.
        </p>
        <button className="crm-btn" onClick={() => setShowNew(true)} data-testid="crm-new-deal-btn">
          <Plus size={16} /> New Deal
        </button>
      </div>

      {err && <div className="crm-card" style={{ color: '#9f1239', marginBottom: 12 }}>{err}</div>}

      {loading ? <div className="crm-card">Loading…</div> : (
        <div className="crm-kanban">
          {STAGE_ORDER.map((s) => {
            const meta = STAGE_META[s];
            const items = grouped[s];
            const value = items.reduce((sum, d) => sum + Number(d.client_quote || 0), 0);
            return (
              <div
                key={s}
                className={`crm-col ${hoverStage === s ? 'drop-target' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setHoverStage(s); }}
                onDragLeave={() => setHoverStage((h) => (h === s ? null : h))}
                onDrop={() => onDrop(s)}
              >
                <div className="crm-col-head">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color }} />
                    <h4>{meta.label}</h4>
                  </div>
                  <span className="count">{items.length}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--crm-muted)', padding: '0 6px 4px', fontWeight: 600 }}>
                  {admin ? `${fmtMoney(value)} pipeline` : `${items.length} deal${items.length === 1 ? '' : 's'}`}
                </div>
                {items.length === 0 && (
                  <div style={{ fontSize: 11.5, color: '#94a3b8', textAlign: 'center', padding: '20px 0' }}>
                    Drop here
                  </div>
                )}
                {items.map((d) => (
                  <Link key={d.id} to={`/crm/deals/${d.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                    <div
                      className={`crm-card-mini ${drag?.id === d.id ? 'dragging' : ''}`}
                      draggable
                      onDragStart={() => onDragStart(d)}
                      onDragEnd={onDragEnd}
                      data-testid={`crm-deal-card-${d.id}`}
                    >
                      <div className="company">{d.company}</div>
                      <div className="project">{d.project_name || '—'}</div>
                      <div className="meta">
                        {admin && <span className="fee">{fmtMoney(d.client_quote)}</span>}
                        <span className="source">{d.contact_type} · {d.source}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            );
          })}
        </div>
      )}

      {showNew && <NewDealModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reload(); }} />}
    </div>
  );
}
